// Lifecycle Management API client — talks to the PHP backend deployed at
// /lifecycle-api/ (a sibling folder to this app's own files, same domain,
// so no CORS setup is needed). See README in the lifecycle-backend folder
// for deployment instructions.
import { STORAGE_KEYS, loadFromStorage, saveToStorage } from './helpers';

const API_BASE = '/lifecycle-api';

export interface LifecycleUser {
  id: number;
  user_key: string;
  name: string;
  email: string;
  role: string;
}

export interface Enquiry {
  id: number;
  service_type: string;
  ref_number: string;
  enquiry_date: string | null;
  customer_name: string | null;
  contact_number: string | null;
  email: string | null;
  contact_via: string | null;
  event_dates: string | null;
  location_venue: string | null;
  suggested_package: string | null;
  follow_up_date: string | null;
  follow_up_status: string | null;
  overall_status: string;
  comments: string | null;
  created_by_name?: string;
  updated_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface Booking {
  id: number;
  enquiry_id: number;
  csr_number: string;
  quote_ref_system?: string|null;
  quote_ref_studio?: string|null;
  invoice_ref_system?: string|null;
  invoice_ref_studio?: string|null;
  current_stage: string;
  event_passed: number;
  customer_name: string;
  service_type: string;
  event_dates: string | null;
  location_venue: string | null;
  created_by_name?: string;
  updated_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface StageHistoryEntry {
  id: number;
  booking_id: number;
  from_stage: string | null;
  to_stage: string;
  note: string | null;
  changed_by_name?: string;
  changed_at: string;
}

// Mirrors the backend's $STAGE_ORDER in stage.php — single source of truth
// for display purposes. The event-day gate sits right after "Event
// Execution"; the backend is the real authority on enforcing it, this is
// just for rendering the stepper.
export const STAGE_ORDER = [
  'Customer Document & Booking',
  'Contractor/Vendor Assignment',
  'Readiness & Planning',
  'Event Execution',
  'Post-Production',
  'Final Delivery',
  'Post-Event',
];

export interface CustomerSpecificDetails {
  id: number;
  booking_id: number;
  customer_poc_name: string | null;
  customer_poc_contact: string | null;
  contractor_poc_name: string | null;
  contractor_poc_contact: string | null;
  second_meeting_date: string | null;
  second_meeting_status: string;
  final_package: string | null;
  addons: string | null;
  total_quoted_amount: string | null;
  advance_received: string | null;
  balance_due: string | null;
  readiness_status: string;
  special_requirements: string | null;
  comments: string | null;
  action_items: string | null;
  social_media_consent: string | null;
  social_post_platform: string | null;
  social_post_timing: string | null;
  social_caption_credit: string | null;
  social_notes: string | null;
  updated_by_name?: string;
  updated_at: string;
}

function getToken(): string | null {
  return loadFromStorage<string | null>(STORAGE_KEYS.LIFECYCLE_TOKEN, null);
}

// If a background lifecycleLogin() is still in flight (see below), every
// other lifecycle-api call waits for it here before checking the token —
// this is what actually closes the race where a page loaded immediately
// after login would read a token that hadn't been saved yet.
let lifecycleLoginPromise: Promise<boolean> | null = null;

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  if (path !== '/login.php' && lifecycleLoginPromise) {
    await lifecycleLoginPromise;
  }
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return data as T;
}

// Called silently right after a successful planner login, using the same
// email/password the person just typed — gives one login for both systems
// without a second login screen. If this fails (e.g. backend not deployed
// yet, or network hiccup), it fails quietly — Lifecycle pages will show
// their own "not connected" state rather than blocking the whole app.
export function lifecycleLogin(email: string, password: string): Promise<boolean> {
  const p = (async () => {
    try {
      const data = await request<{ token: string; user: LifecycleUser }>('/login.php', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      saveToStorage(STORAGE_KEYS.LIFECYCLE_TOKEN, data.token);
      return true;
    } catch {
      saveToStorage(STORAGE_KEYS.LIFECYCLE_TOKEN, null);
      return false;
    } finally {
      lifecycleLoginPromise = null;
    }
  })();
  lifecycleLoginPromise = p;
  return p;
}

export function lifecycleLogout(): void {
  saveToStorage(STORAGE_KEYS.LIFECYCLE_TOKEN, null);
}

export function isLifecycleConnected(): boolean {
  return !!getToken();
}

// Diagnostic-only helpers — let a page show exactly what happened on screen
// (token state, raw HTTP status per call) without needing browser DevTools.
export function getConnectionDebugInfo(): { hasToken: boolean; tokenPreview: string } {
  const t = getToken();
  return { hasToken: !!t, tokenPreview: t ? `${t.slice(0, 6)}…${t.slice(-4)}` : '(none)' };
}

export async function debugFetchEnquiries(serviceType: string): Promise<{ service: string; status: number; ok: boolean; count: number; raw: string }> {
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  try {
    const res = await fetch(`${API_BASE}/enquiries.php?service_type=${encodeURIComponent(serviceType)}`, { headers });
    const text = await res.text();
    let count = -1;
    try { count = (JSON.parse(text).enquiries || []).length; } catch {}
    return { service: serviceType, status: res.status, ok: res.ok, count, raw: text.slice(0, 200) };
  } catch (e: any) {
    return { service: serviceType, status: 0, ok: false, count: -1, raw: e?.message || 'network error' };
  }
}

export async function fetchEnquiries(serviceType: string): Promise<Enquiry[]> {
  const data = await request<{ enquiries: Enquiry[] }>(`/enquiries.php?service_type=${encodeURIComponent(serviceType)}`);
  return data.enquiries;
}

export async function createEnquiry(fields: Partial<Enquiry> & { service_type: string }): Promise<Enquiry> {
  const data = await request<{ enquiry: Enquiry }>('/enquiries.php', {
    method: 'POST',
    body: JSON.stringify(fields),
  });
  return data.enquiry;
}

export async function updateEnquiry(id: number, fields: Partial<Enquiry>): Promise<Enquiry> {
  const data = await request<{ enquiry: Enquiry }>(`/enquiries.php?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(fields),
  });
  return data.enquiry;
}

export async function fetchBookings(): Promise<Booking[]> {
  const data = await request<{ bookings: Booking[] }>('/bookings.php');
  return data.bookings;
}

export async function fetchBooking(id: number): Promise<Booking> {
  const data = await request<{ booking: Booking }>(`/bookings.php?id=${id}`);
  return data.booking;
}

export async function createBooking(enquiryId: number, quoteRefStudio?: string, invoiceRefStudio?: string): Promise<Booking> {
  const data = await request<{ booking: Booking }>('/bookings.php', {
    method: 'POST',
    body: JSON.stringify({ enquiry_id: enquiryId, quote_ref_studio: quoteRefStudio||null, invoice_ref_studio: invoiceRefStudio||null }),
  });
  return data.booking;
}

export async function fetchStageHistory(bookingId: number): Promise<StageHistoryEntry[]> {
  const data = await request<{ history: StageHistoryEntry[] }>(`/stage.php?booking_id=${bookingId}`);
  return data.history;
}

export async function moveStage(bookingId: number, toStage: string, note?: string): Promise<Booking> {
  const data = await request<{ booking: Booking }>('/stage.php', {
    method: 'POST',
    body: JSON.stringify({ booking_id: bookingId, to_stage: toStage, note: note || null }),
  });
  return data.booking;
}

export async function fetchCustomerSpecificDetails(bookingId: number): Promise<CustomerSpecificDetails | null> {
  const data = await request<{ details: CustomerSpecificDetails | null }>(`/customer_specific.php?booking_id=${bookingId}`);
  return data.details;
}

export async function saveCustomerSpecificDetails(bookingId: number, fields: Partial<CustomerSpecificDetails>): Promise<CustomerSpecificDetails> {
  const data = await request<{ details: CustomerSpecificDetails }>(`/customer_specific.php?booking_id=${bookingId}`, {
    method: 'PUT',
    body: JSON.stringify(fields),
  });
  return data.details;
}
